import { useEffect, useMemo, useRef, useState } from "react";

const OTHER_SELECT_VALUE = "__OTHER__";

const cleanSelectOptionValue = (value) =>
  String(value || "").replace(/\s*[[(][^\])]*[\])]\s*$/g, "").trim();

export const cleanSearchableSelectOptions = (options = []) =>
  [...new Set(options.map(cleanSelectOptionValue).filter(Boolean))];

export default function SearchableSelect({
  allowCustom = true,
  ariaLabel,
  className = "",
  customPlaceholder,
  name,
  onChange,
  options = [],
  placeholder = "Select",
  value,
}) {
  const cleanedOptions = useMemo(() => cleanSearchableSelectOptions(options), [options]);
  const [isOpen, setIsOpen] = useState(false);
  const [isCustom, setIsCustom] = useState(Boolean(value) && !cleanedOptions.includes(value));
  const [query, setQuery] = useState(value || "");
  const wrapperRef = useRef(null);
  const menuId = `${name || ariaLabel || placeholder}-select-listbox`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return cleanedOptions;
    return cleanedOptions.filter((option) => option.toLowerCase().includes(normalizedQuery));
  }, [cleanedOptions, normalizedQuery]);

  useEffect(() => {
    setQuery(value || "");
    setIsCustom(Boolean(value) && !cleanedOptions.includes(value));
  }, [cleanedOptions, value]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const chooseOption = (nextValue) => {
    setIsCustom(false);
    setQuery(nextValue);
    setIsOpen(false);
    onChange(nextValue);
  };
  const chooseCustom = () => {
    setIsCustom(true);
    setIsOpen(false);
    onChange(query.trim());
  };
  const handleInputChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setIsOpen(true);
    if (!nextQuery.trim()) {
      setIsCustom(false);
      onChange("");
      return;
    }
    if (isCustom) onChange(nextQuery);
  };
  const handleBlur = () => {
    if (isCustom) onChange(query.trim());
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (filteredOptions.length && !isCustom) {
        chooseOption(filteredOptions[0]);
      } else if (allowCustom) {
        chooseCustom();
      }
    }
    if (event.key === "Escape") setIsOpen(false);
  };

  return (
    <div className={`searchable-select ${className}`.trim()} ref={wrapperRef}>
      <input
        aria-autocomplete="list"
        aria-controls={menuId}
        aria-expanded={isOpen}
        aria-label={ariaLabel || placeholder}
        name={name}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={isCustom ? customPlaceholder || placeholder : placeholder}
        role="combobox"
        type="text"
        value={query}
      />
      {isOpen ? (
        <div className="searchable-select-menu" id={menuId} role="listbox">
          {filteredOptions.length ? filteredOptions.map((option) => (
            <button
              className={option === value ? "is-selected" : ""}
              key={option}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => chooseOption(option)}
              aria-selected={option === value}
              role="option"
              type="button"
            >
              {option}
            </button>
          )) : (
            <span className="searchable-select-empty">No matches</span>
          )}
          {allowCustom ? (
            <button
              className="searchable-select-other"
              onMouseDown={(event) => event.preventDefault()}
              onClick={chooseCustom}
              type="button"
              value={OTHER_SELECT_VALUE}
            >
              Other
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
